import { apiFetch } from './client'
import type {
  ConsultationCreate,
  ConsultationDetail,
  ConsultationListItem,
  ConsultationStatus,
  ConsultationUpdate,
  Diagnosis,
  Prescription,
  SoapNote,
  Vaccination,
  VaccinationCreate,
  VitalSigns,
} from '../types/clinical'

// ── Consultations ─────────────────────────────────────────────────────────────

export async function listRecentConsultations(limit = 10, status?: ConsultationStatus): Promise<ConsultationListItem[]> {
  const res = await apiFetch(`/api/consultations/recent?limit=${limit}${status ? `&status=${status}` : ''}`)
  if (!res.ok) throw new Error('Failed to load recent consultations')
  return res.json() as Promise<ConsultationListItem[]>
}

export async function listConsultations(patientId: number, params?: { limit?: number; offset?: number }): Promise<ConsultationListItem[]> {
  const qs = new URLSearchParams()
  if (params?.limit !== undefined) qs.set('limit', String(params.limit))
  if (params?.offset !== undefined) qs.set('offset', String(params.offset))
  const res = await apiFetch(`/api/patients/${patientId}/consultations?${qs}`)
  if (!res.ok) throw new Error('Failed to load consultations')
  return res.json() as Promise<ConsultationListItem[]>
}

export async function createConsultation(patientId: number, data: ConsultationCreate): Promise<ConsultationDetail> {
  const res = await apiFetch(`/api/patients/${patientId}/consultations`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to create consultation')
  }
  return res.json() as Promise<ConsultationDetail>
}

export async function getConsultation(id: number): Promise<ConsultationDetail> {
  const res = await apiFetch(`/api/consultations/${id}`)
  if (res.status === 404) throw new Error('Consultation not found')
  if (!res.ok) throw new Error('Failed to load consultation')
  return res.json() as Promise<ConsultationDetail>
}

export async function updateConsultation(id: number, data: ConsultationUpdate): Promise<ConsultationDetail> {
  const res = await apiFetch(`/api/consultations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to update consultation')
  }
  return res.json() as Promise<ConsultationDetail>
}

// ── SOAP ──────────────────────────────────────────────────────────────────────

export async function upsertSoap(consultationId: number, data: Partial<SoapNote>): Promise<SoapNote> {
  const res = await apiFetch(`/api/consultations/${consultationId}/soap`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  if (!res.ok) throw new Error('Failed to save SOAP note')
  return res.json() as Promise<SoapNote>
}

// ── Vitals ────────────────────────────────────────────────────────────────────

export async function upsertVitals(consultationId: number, data: Partial<VitalSigns>): Promise<VitalSigns> {
  const res = await apiFetch(`/api/consultations/${consultationId}/vitals`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  if (!res.ok) throw new Error('Failed to save vital signs')
  return res.json() as Promise<VitalSigns>
}

// ── Diagnoses ─────────────────────────────────────────────────────────────────

export async function addDiagnosis(consultationId: number, data: { description: string; is_primary?: boolean }): Promise<Diagnosis> {
  const res = await apiFetch(`/api/consultations/${consultationId}/diagnoses`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to add diagnosis')
  }
  return res.json() as Promise<Diagnosis>
}

export async function removeDiagnosis(id: number): Promise<void> {
  const res = await apiFetch(`/api/diagnoses/${id}`, { method: 'DELETE' })
  if (!res.ok) throw new Error('Failed to remove diagnosis')
}

// ── Prescriptions ─────────────────────────────────────────────────────────────

export async function addPrescription(consultationId: number, data: {
  medication_name: string
  dosage?: string | null
  frequency?: string | null
  duration?: string | null
  instructions?: string | null
  product_id?: number | null
  stock_batch_id?: number | null
  quantity_dispensed?: string | null
}): Promise<Prescription> {
  const res = await apiFetch(`/api/consultations/${consultationId}/prescriptions`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to add prescription')
  }
  return res.json() as Promise<Prescription>
}

export async function removePrescription(id: number): Promise<void> {
  const res = await apiFetch(`/api/prescriptions/${id}`, { method: 'DELETE' })
  if (!res.ok) throw new Error('Failed to remove prescription')
}

// ── Vaccinations ──────────────────────────────────────────────────────────────

export async function listVaccinations(patientId: number): Promise<Vaccination[]> {
  const res = await apiFetch(`/api/patients/${patientId}/vaccinations`)
  if (!res.ok) throw new Error('Failed to load vaccinations')
  return res.json() as Promise<Vaccination[]>
}

export async function createVaccination(patientId: number, data: VaccinationCreate): Promise<Vaccination> {
  const res = await apiFetch(`/api/patients/${patientId}/vaccinations`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to record vaccination')
  }
  return res.json() as Promise<Vaccination>
}
